import { AnalyticsEventName } from '@dupe/common/constants'
import { getShopKey } from '@dupe/common/helpers'
import {
  SerpLensResponse,
  SortCriteria,
  type VisualMatch,
} from '@dupe/common/types'
import clsx from 'clsx'
import { motion } from 'framer-motion'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { sortResults } from '~/src/lib/sortResults'
import DupeResultCard from '~/src/components/DupeResultCard'
import SkeletonLoader from '~/src/components/SkeletonLoader'
import type { ResultsResp } from '~/src/db/queryResultsResp'
import useAnalytics from '~/src/hooks/useAnalytics'
import { zarazTrack } from '~/src/lib/analytics/zaraz'
import { useClickSource } from '~/src/context/ClickSourceContext'
import { useResultSortContext } from '~/src/context/ResultSortContext'
import { cn } from '~/src/lib/utils'
import { PARTNER_PIN_SORT_CRITERIA } from '~/src/components/chat/organicOrder'

interface Props {
  source?: 'dupe' | 'drawer-dupe' | 'seo-dupe' | 'sidepanel' | 'drawer-ourpicks'
  loading?: boolean
  processing?: boolean
  resultsData: Partial<ResultsResp> | null
  visualMatches: SerpLensResponse['visual_matches']
  hasOurPicks?: boolean
  hideFavicons?: boolean
  disableCardActions?: boolean
  dontBlock?: boolean
  initialInView?: boolean
  selectedCountry?: string
  sortCriteria?: SortCriteria
  query?: string | null
  query_category?: string | null
}

const DupeResults: React.FC<Props> = ({
  source: baseSource = 'dupe',
  loading,
  processing,
  resultsData,
  visualMatches: _visualMatches,
  hasOurPicks,
  hideFavicons,
  disableCardActions,
  dontBlock,
  initialInView,
  selectedCountry,
  sortCriteria = SortCriteria.Recommended,
  query,
  query_category,
}) => {
  const analytics = useAnalytics()
  // Default-sort arm slot for analytics tagging. `sortCriteria` already
  // arrives as a prop; the variant comes from the surface-level provider, and
  // has been null since `rerank-default-sort` was removed on 2026-09-16.
  // `sortReady` still gates the stamp so a click landing before the surface's
  // sort settles can't leak pre-resolution metadata into a per-arm slice
  // (mirrors useResultImpression).
  const {
    defaultSortVariant,
    sortReady,
    inputType,
    sortCriteria: contextSortCriteria,
  } = useResultSortContext()
  // Partner-pin `pin2` on the `/[hash]` page: the provider stamps 'partnerPin',
  // and non-pinned picks render in this grid at their served position instead
  // of the picks list. They are still picks, so they keep the pick attribution
  // (`isOurPick` → redirect `pick`, `ourPicks` on the click) they carry in
  // control. Outside pin2 this is false and nothing changes.
  const partnerPinGrid = contextSortCriteria === PARTNER_PIN_SORT_CRITERIA
  const router = useRouter()
  const linkParam = router.query.link as string
  // Prefix the source via ClickSourceContext when rendered inside a
  // surface-specific wrapper (e.g. extension sidepanel -> 'ext-drawer-dupe').
  const source = useClickSource(baseSource)

  const isLoading = loading || processing

  const visualMatches = useMemo(() => {
    // We MUST have either a link or a google_product_id
    const filteredMatches = _visualMatches.filter(
      (match) => !!match.link || !!match.google_product_id
    )

    // When sorting by price, hide "Our Picks" (matches with pick property)
    if (sortCriteria === SortCriteria.Price) {
      const withoutPicks = filteredMatches.filter((match) => !match.pick)
      return sortResults(withoutPicks, sortCriteria)
    }

    // For recommended sorting, keep original order and picks
    return sortResults(filteredMatches, sortCriteria)
  }, [_visualMatches, sortCriteria])

  const placeholderLength = visualMatches?.length || 10
  const placeholderItems = useMemo(
    () => Array.from({ length: placeholderLength }).map((_, i) => ({ id: i })),
    [placeholderLength]
  )

  // Server already sorts the results, so we'll use them directly
  const sortedVisualMatches = visualMatches

  // Track extension wall shown once per page when any blocked result is rendered
  const [extensionWallShownTracked, setExtensionWallShownTracked] =
    useState(false)

  const trackExtensionWallShown = useCallback(() => {
    if (!extensionWallShownTracked) {
      analytics.track(AnalyticsEventName.ResultsExtensionWallShown, {
        hash: resultsData?.hash,
        shopKey: resultsData?.shopKey,
        source,
        totalResults: visualMatches.length,
      })
      setExtensionWallShownTracked(true)
    }
  }, [
    extensionWallShownTracked,
    analytics,
    resultsData?.hash,
    resultsData?.shopKey,
    source,
    visualMatches.length,
  ])

  const trackExtensionWallClick = useCallback(
    (index: number, match: VisualMatch, blockedHref: string) => {
      analytics.track(AnalyticsEventName.ResultsExtensionWallClick, {
        hash: resultsData?.hash,
        index,
        source: match.source,
        shopKey: resultsData?.shopKey,
        blockedHref,
      })
    },
    [analytics, resultsData?.hash, resultsData?.shopKey]
  )

  // Track blocking status for each result card
  const [blockedResults, setBlockedResults] = useState<Set<number>>(new Set())

  const handleBlockingStatusChange = useCallback(
    (index: number, isBlocked: boolean) => {
      setBlockedResults((prev) => {
        const newSet = new Set(prev)
        if (isBlocked) {
          newSet.add(index)
        } else {
          newSet.delete(index)
        }
        return newSet
      })
    },
    []
  )

  const renderVisualMatches = (match: VisualMatch, index: number) => {
    if (!match?.link && !match?.google_product_id) return null

    const shopKey = match.shopKey || getShopKey(match.link)

    const linkOrGoogleProductId = (match.link ||
      match.google_product_id) as string

    return (
      <motion.div
        id={encodeURIComponent(linkOrGoogleProductId)}
        key={`${index}.${match.position}.${linkOrGoogleProductId}`}
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <DupeResultCard
          index={index}
          source={source}
          resultsData={resultsData}
          match={match}
          shouldHighlight={match.link === linkParam}
          selectedCountry={selectedCountry}
          dsv={sortReady ? defaultSortVariant : null}
          {...(partnerPinGrid && match.pick ? { isOurPick: true } : {})}
          showSaleIcon={match.tag === 'SALE'}
          hasOurPicks={hasOurPicks}
          hideFavicons={hideFavicons}
          processing={processing}
          disableCardActions={disableCardActions}
          dontBlock={dontBlock}
          initialInView={initialInView}
          onExtensionWallShown={trackExtensionWallShown}
          onExtensionWallClick={trackExtensionWallClick}
          onBlockingStatusChange={(isBlocked) =>
            handleBlockingStatusChange(index, isBlocked)
          }
          query={query}
          query_category={query_category}
          className={cn(
            'px-4 py-4 md:px-6 md:py-6',
            'border-b border-black/10 dark:border-white/10',
            {
              'border-transparent': index === visualMatches.length - 1,
            }
          )}
          onOutboundClick={({ clickId }) => {
            // Don't fire DupeResultClicked for blocked/walled items
            // Only fire ResultsExtensionWallClick for those (handled in DupeResultCard)
            if (!blockedResults.has(index)) {
              analytics.track(
                AnalyticsEventName.DupeResultClicked,
                {
                  source,
                  // Joins this event to its `link_clicks` row and from there to
                  // commission. Null when the click beat hydration.
                  click_id: clickId,
                  shoppingSource: match.shopping_source,
                  hash: resultsData?.hash,
                  shopKey,
                  result: match,
                  resultIndex: index,
                  isPartner: !!match?.verified,
                  sort_criteria: sortReady
                    ? partnerPinGrid
                      ? PARTNER_PIN_SORT_CRITERIA
                      : sortCriteria
                    : null,
                  ...(partnerPinGrid && match.pick ? { ourPicks: true } : {}),
                  default_sort_variant: sortReady ? defaultSortVariant : null,
                  input_type: inputType,
                  leg: match.leg ?? null,
                  served_index: match.servedIndex ?? null,
                  natural_index: match.naturalIndex ?? null,
                  displayed_index: index,
                  visual_score: match.visualScore ?? null,
                },
                {
                  sendToPlausible: true,
                  sendToTinybird: true,
                  sendToFb: true,
                }
              )

              // Google Ads imports conversions from this GA4 event name (see
              // docs/features/active/zaraz-migration/technical-spec.md) — no
              // gtag left on the page, so it now rides the Zaraz GA4 managed
              // component instead of a direct sendGAEvent call.
              zarazTrack('conversion_event_outbound_click', {
                hash: resultsData?.hash,
                shopKey,
              })
            }
          }}
        />
      </motion.div>
    )
  }

  // Scroll to linkParam
  useEffect(() => {
    if (isLoading) return
    if (!linkParam) return

    const timer = setTimeout(() => {
      const encodedLink = encodeURIComponent(linkParam)
      const linkElement = document.getElementById(encodedLink)

      if (linkElement) {
        linkElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [linkParam, isLoading])

  return (
    <div id='resultstop'>
      {isLoading &&
        placeholderItems.map((item, index) => (
          <div
            key={item.id}
            className={clsx(
              'flex space-x-2 border-b border-black/10 px-4 py-4 md:space-x-4 md:px-6 md:py-8',
              {
                'border-transparent': index === placeholderItems.length - 1,
              }
            )}
          >
            <div className='shrink-0'>
              <SkeletonLoader
                height='100%'
                width='100%'
                className='min-h-[115px] min-w-[115px] rounded-lg md:min-h-[150px] md:min-w-[150px]'
              />
            </div>
            <div className='flex w-full flex-col'>
              <div className='flex-1 space-y-2'>
                <SkeletonLoader height={20} width={200} className='rounded' />
                <SkeletonLoader height={20} className='rounded' />
              </div>
              <div>
                <SkeletonLoader height={33} width={125} className='rounded' />
              </div>
            </div>
          </div>
        ))}

      {!isLoading && sortedVisualMatches.map(renderVisualMatches)}

      <div className='border-t border-t-black/10 p-4 text-center md:p-6 dark:border-white/10'>
        <div className='text-sm opacity-40'>
          <p>
            We do not provide evaluations or comparisons regarding quality of
            products.
          </p>
          <p>Some links are commissionable.</p>
        </div>
      </div>
    </div>
  )
}

export default DupeResults
